/**
 * WorksheetHero — big preview at the top of the Detail screen.
 *
 * Per doc 07 §3:
 *  - 360×500 aspect (we use aspectRatio=5:7 for portrait mockup)
 *  - Shows worksheet.image_url (mockup) when available, falls back to
 *    worksheet.thumbnail_url, falls back to a tinted card with the
 *    activity emoji as visual cue
 *  - Tinted brand-purple background, 16px radius
 *
 * Y1 uses the activity emoji as a placeholder when the real preview
 * image fails to load (lazy load + error fallback).
 */

import { Image, StyleSheet, View } from 'react-native';

import { Radii, Spacing } from '@/constants/theme';
import { activityIcon } from '@/lib/icons';
import { AppIcon } from '@/components/app-icon';
import type { WorksheetDetail } from '@/lib/api/worksheet';

interface WorksheetHeroProps {
  worksheet: WorksheetDetail;
}

export function WorksheetHero({ worksheet }: WorksheetHeroProps) {
  const imageUrl = worksheet.image_url ?? worksheet.thumbnail_url;
  const icon = activityIcon(worksheet.activity_label);

  return (
    <View style={styles.container}>
      <View style={styles.imageWrap}>
        {imageUrl ? (
          <Image
            source={{ uri: imageUrl }}
            style={styles.image}
            resizeMode="contain"
            accessibilityIgnoresInvertColors
          />
        ) : (
          <View style={styles.placeholder}>
            <AppIcon source={icon} size={96} />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    paddingHorizontal: Spacing.four,
  },
  imageWrap: {
    width: '100%',
    aspectRatio: 5 / 7, // portrait, similar to A4 paper
    maxHeight: 360,
    backgroundColor: '#f5f3ee',
    borderRadius: Radii.medium,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e5e5e5',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    // Netral — brand purple hanya aksen (Fase 1 redesign).
    backgroundColor: '#f5f3ee',
  },
});
